/**
 * 基础配置 - 线索推送 接口定义
 */
import { apiDelete, apiDownload, apiGet, apiPost, apiPut } from '@/utils/request';
import { parsePaginatedResponse } from '@/utils/apiResponse';
import { LARGE_PAGE_SIZE } from '@/constants/api';
import type { ApiPushFieldsEnums, QwRoomGroup, TestSendResponse, WebhookEnums } from '@/types/push';

/** 下载接口沿用列表筛选参数，过滤空值并拼到 GET 查询串 */
function exportUrl(path: string, params: Record<string, unknown>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  });
  const search = query.toString();
  return search ? `${path}?${search}` : path;
}

/** 客资接口推送配置 API */
export const userPushSettingsApi = {
  /** 分页查询配置列表 */
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/user-push-settings', params).then((d) => parsePaginatedResponse(d)),
  /** 配置详情 */
  detail: (id: number) => apiGet(`/user-push-settings/${id}`),
  /** 创建配置 */
  create: (data: Record<string, unknown>) => apiPost('/user-push-settings', data),
  /** 更新配置 */
  update: (id: number, data: Record<string, unknown>) => apiPut(`/user-push-settings/${id}`, data),
  /** 删除配置 */
  delete: (id: number) => apiDelete(`/user-push-settings/${id}`),
  /** 可选字段枚举（必填 / 可选 / 实体字段、目标系统、签名变量） */
  fields: () => apiGet<ApiPushFieldsEnums>('/user-push-settings/fields'),
  /** 推送日志列表 */
  pushLogs: (params: Record<string, unknown> = {}) =>
    apiGet('/user-push-settings/push-logs', { per_page: LARGE_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  /** 导出符合筛选条件的 CRM 接口推送记录 */
  exportPushLogs: (params: Record<string, unknown> = {}, filename?: string) =>
    apiDownload(exportUrl('/user-push-settings/push-logs/export', params), { filename }),
  /** 手动重推 */
  repush: (data: Record<string, unknown>) => apiPost('/user-push-settings/repush', data),
  /** 查看客资推送记录明文；每次调用均由后端记录审计日志 */
  revealPii: (id: number) =>
    apiPost<{ id: number; push_data: Record<string, unknown>; response: string }>(
      `/user-push-settings/push-logs/${id}/pii-reveal`,
    ),
};

/** Webhook 推送客资群模板 API */
export const webhookTemplatesApi = {
  /** 模板列表（分页） */
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/webhook-templates', params).then((d) => parsePaginatedResponse(d)),
  /** 模板详情 */
  detail: (id: number) => apiGet(`/webhook-templates/${id}`),
  /** 创建模板 */
  create: (data: Record<string, unknown>) => apiPost('/webhook-templates', data),
  /** 更新模板 */
  update: (id: number, data: Record<string, unknown>) => apiPut(`/webhook-templates/${id}`, data),
  /** 删除模板 */
  delete: (id: number) => apiDelete(`/webhook-templates/${id}`),
  /** 当前应用可选的企微外部群（已绑定本应用的群） */
  qwRoomGroups: () => apiGet<QwRoomGroup[]>('/webhook-templates/qw-room-groups'),
  /** 获取枚举值（客资条件 / 推送平台 / 消息类型 / 消息内容可插入字段） */
  enums: () => apiGet<WebhookEnums>('/webhook-templates/enums'),
  /** 一键测试推送：提交编辑中的话术 + 群信息，后端同步推送并回传每个目标的结果 */
  testSend: (data: Record<string, unknown>) =>
    apiPost<TestSendResponse>('/webhook-templates/test-send', data),
  /** 推送日志列表（分页） */
  pushLogs: (params: Record<string, unknown> = {}) =>
    apiGet('/webhook-templates/push-logs', { per_page: LARGE_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  /** 导出符合筛选条件的客资群推送记录 */
  exportPushLogs: (params: Record<string, unknown> = {}, filename?: string) =>
    apiDownload(exportUrl('/webhook-templates/push-logs/export', params), { filename }),
  /** 手动重推 */
  repush: (data: Record<string, unknown>) => apiPost('/webhook-templates/repush', data),
  /** 查看群推送记录明文；每次调用均由后端记录审计日志 */
  revealPii: (id: number) =>
    apiPost<{
      id: number;
      user_phone: string;
      user_weixin: string;
      content: string;
      response: string;
    }>(`/webhook-templates/push-logs/${id}/pii-reveal`),
};
